import { useEffect, useMemo } from "react";
import { CoverStory, SEO, SoundPopup } from "@components";
import { FaustTemplate, useFaustQuery } from "@faustwp/core";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { getBasicQueryVars, getLanguageQueryVar, getPageTitle } from "@/core/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { GetCoverStoriesQuery, GetCoverStoryDetailQuery, GetCoverStoryOptionsQuery } from "@graphqlTypes";
import { CoverStoriesQuery, CoverStoryDetailQuery, CoverStoryOptionsQuery } from "@/queries/CoverStory";
import { CoverStoryProvider } from "@/providers/CoverStoryContext";
import { useDebug } from "@/utils/useDebugLog";

const CoverStoryPage: FaustTemplate<any> = (props: FaustPageProps<any>) => {
  const { pageWrapperData, loading } = props;
  const { log } = useDebug({ name: 'single-cover-story', bgColor: '#F3BAD6', enabled: false });

  // Hooks
  const { setHighlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();

  // Site settings
  const { siteSettings: { customSettings: { siteTitle }} } = pageWrapperData;

  // Data queries
  const pageData = useFaustQuery<GetCoverStoryDetailQuery>(CoverStoryDetailQuery);
  const optionsData = useFaustQuery<GetCoverStoryOptionsQuery & { coverStoryLandingPage?: { uri?: string } }>(CoverStoryOptionsQuery);
  const listData = useFaustQuery<GetCoverStoriesQuery>(CoverStoriesQuery);

  // Extract page data
  const { title, gameData, databaseId } = pageData?.rhCoverStory || {};
  const data = { ...gameData, databaseId };

  // Extract options data
  const { coverStoryOptions: { coverStoryOptions } = {} } = optionsData || {};
  const { coverStoryDetailOgImage } = coverStoryOptions || {};
  const landingPageUri = optionsData?.coverStoryLandingPage?.uri;

  // Extract and process list data
  const { coverStories } = listData || {};

  // SEO
  const seoTitle = getPageTitle(siteTitle, title);

  log('pageData:', { coverStories, coverStoryOptions, gameData });

  // --------------------

  // Set highlight color to pink for Cover Story pages
  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.PINK);
  }, []);

  // Track route change for analytics
  useTrackRouteChange("watch");

  // Handle preview loading state
  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  // -------------------- Render

  if (loading) return;

  return (
    <>
      <SEO title={seoTitle} image={coverStoryDetailOgImage} />
      <CoverStoryProvider options={coverStoryOptions}>
        <CoverStory
          coverStories={coverStories}
          gameData={data}
          options={coverStoryOptions}
          title={title}
          pageUri={landingPageUri}
        />
      </CoverStoryProvider>
      {/* <SoundPopup headlineOverride={soundPopupOverride} /> No sound for now */}
    </>
  );

};

CoverStoryPage.queries = [
  { query: CoverStoryDetailQuery, variables: getBasicQueryVars },
  { query: CoverStoryOptionsQuery, variables: getLanguageQueryVar },
  {
    query: CoverStoriesQuery,
    variables: (seedNode) => {
      const { databaseId } = seedNode;
      const { language }= getLanguageQueryVar(seedNode);
      return {
        language,
        notIn: [databaseId],
      };
    }
  },
]

export default CoverStoryPage;
